import { useEffect, useRef, useState } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';
import { getRadarBboxCoordinates } from './radarLayerUtils';
import type { RadarTile } from './useDataFetching';
import {
  transitionToFrame,
  removeTransitionLayers,
  updateTransitionOpacity,
  createTransitionState,
  type TransitionLayerConfig,
  type TransitionState,
  type ImageCacheLike,
} from './radarTransition';
import { decodePngdTile, colorizeTile, revokeBlobUrl } from './numericTileDecoder';
import { getColormapById } from './numericColormaps';

export interface UseNumericRadarLayerParams {
  mapRef: React.RefObject<MapboxMap | null>;
  selectedEvent: EventWithCircuit | null;
  extendedTiles: RadarTile[];
  currentIndex: number;
  radarOpacity: number;
  fps: number;
  styleVersion: number;
  mapTileProvider: string;
  numericNoiseFloor: number;
  numericColormap: string;
  radarNumericSourceId: string;
  radarNumericLayerId: string;
  radarNumericSourceId2: string;
  radarNumericLayerId2: string;
  imageCacheRef: React.RefObject<ImageCacheLike>;
}

const MAX_RETRIES = 20;
const RETRY_DELAY = 50;

export function useNumericRadarLayer({
  mapRef,
  selectedEvent,
  extendedTiles,
  currentIndex,
  radarOpacity,
  fps,
  styleVersion,
  mapTileProvider,
  numericNoiseFloor,
  numericColormap,
  radarNumericSourceId,
  radarNumericLayerId,
  radarNumericSourceId2,
  radarNumericLayerId2,
  imageCacheRef,
}: UseNumericRadarLayerParams): { currentBlobUrl: string | null } {
  const transitionStateRef = useRef<TransitionState>(createTransitionState());
  const [currentBlobUrl, setCurrentBlobUrl] = useState<string | null>(null);
  const configRef = useRef<TransitionLayerConfig>({
    sourceIdA: radarNumericSourceId,
    layerIdA: radarNumericLayerId,
    sourceIdB: radarNumericSourceId2,
    layerIdB: radarNumericLayerId2,
  });

  // Track current blob URLs for cleanup
  const activeBlobUrlRef = useRef<string | null>(null);
  const lastFrameUrlRef = useRef<string | null>(null);
  const lastNoiseFloorRef = useRef<number>(numericNoiseFloor);
  const lastColormapRef = useRef<string>(numericColormap);

  useEffect(() => {
    configRef.current = {
      sourceIdA: radarNumericSourceId,
      layerIdA: radarNumericLayerId,
      sourceIdB: radarNumericSourceId2,
      layerIdB: radarNumericLayerId2,
    };
  }, [radarNumericSourceId, radarNumericLayerId, radarNumericSourceId2, radarNumericLayerId2]);

  useEffect(() => {
    transitionStateRef.current = createTransitionState();
  }, [styleVersion]);

  // Remove layers when provider changes away from numeric
  useEffect(() => {
    if (mapTileProvider !== 'radar-l2-numeric') {
      const map = mapRef.current;
      if (map) {
        removeTransitionLayers(map, configRef.current, transitionStateRef.current);
      }
      if (activeBlobUrlRef.current) {
        revokeBlobUrl(activeBlobUrlRef.current);
        activeBlobUrlRef.current = null;
        setCurrentBlobUrl(null);
      }
    }
  }, [mapTileProvider, mapRef]);

  // Main frame update effect
  useEffect(() => {
    if (mapTileProvider !== 'radar-l2-numeric') return;

    const map = mapRef.current;
    if (!map || !selectedEvent) return;

    if (extendedTiles.length === 0) {
      removeTransitionLayers(map, configRef.current, transitionStateRef.current);
      return;
    }

    const currentTile = extendedTiles[currentIndex];
    if (!currentTile) return;

    const coordinates = getRadarBboxCoordinates(selectedEvent as EventWithCircuit);
    const colormap = getColormapById(numericColormap);

    let cancelled = false;

    const processAndDisplay = async () => {
      if (cancelled) return;

      try {
        const decoded = await decodePngdTile(currentTile.url);
        if (cancelled) return;

        const blobUrl = await colorizeTile(decoded, numericNoiseFloor, colormap);
        if (cancelled) {
          revokeBlobUrl(blobUrl);
          return;
        }

        // Revoke previous blob URL
        if (activeBlobUrlRef.current && activeBlobUrlRef.current !== blobUrl) {
          revokeBlobUrl(activeBlobUrlRef.current);
        }
        activeBlobUrlRef.current = blobUrl;
        setCurrentBlobUrl(blobUrl);
        lastFrameUrlRef.current = currentTile.url;
        lastNoiseFloorRef.current = numericNoiseFloor;
        lastColormapRef.current = numericColormap;

        // Wait for style to be loaded with retry
        let retryCount = 0;
        const applyUpdate = () => {
          if (cancelled) return;
          try {
            if (!map.isStyleLoaded()) {
              retryCount++;
              if (retryCount < MAX_RETRIES) {
                setTimeout(applyUpdate, RETRY_DELAY);
              }
              return;
            }
          } catch {
            return;
          }

          transitionToFrame(
            map,
            configRef.current,
            transitionStateRef.current,
            blobUrl,
            coordinates,
            radarOpacity,
            fps,
            imageCacheRef.current,
          );
        };

        applyUpdate();
      } catch (err) {
        console.warn('[NumericRadarLayer] Failed to process tile:', err);
      }
    };

    processAndDisplay();

    return () => {
      cancelled = true;
    };
  }, [currentIndex, extendedTiles, selectedEvent, styleVersion, mapTileProvider, numericNoiseFloor, numericColormap, mapRef, radarOpacity, fps, imageCacheRef, radarNumericSourceId, radarNumericLayerId, radarNumericSourceId2, radarNumericLayerId2]);

  // Re-colorize when noise floor or colormap changes without changing frame
  useEffect(() => {
    if (mapTileProvider !== 'radar-l2-numeric') return;

    const noiseFloorChanged = numericNoiseFloor !== lastNoiseFloorRef.current;
    const colormapChanged = numericColormap !== lastColormapRef.current;

    if (!noiseFloorChanged && !colormapChanged) return;
    // The main effect already handles re-rendering since numericNoiseFloor and
    // numericColormap are in its dependency array. This ref update ensures the
    // lastX refs track the latest values for comparison.
    lastNoiseFloorRef.current = numericNoiseFloor;
    lastColormapRef.current = numericColormap;
  }, [numericNoiseFloor, numericColormap, mapTileProvider]);

  // Opacity slider changes
  useEffect(() => {
    if (mapTileProvider !== 'radar-l2-numeric') return;
    const map = mapRef.current;
    if (!map) return;

    updateTransitionOpacity(
      map,
      configRef.current,
      transitionStateRef.current,
      radarOpacity,
    );
  }, [radarOpacity, mapTileProvider, mapRef, radarNumericLayerId]);

  return { currentBlobUrl };
}
